import type { AsyncReturnType } from "type-fest";
import type {
  TimingEvent,
  TimingSpan,
  TimingSpanWithEndTime,
  TimingSpanWithoutEndTime,
  TimingStrategy,
} from "../types";

type DevtoolsDetails = { devtools: Record<string, unknown> };

/**
 * Strategy implementation for browser user timing.
 */
export class BrowserUserTimingStrategy implements TimingStrategy {
  public constructor(private readonly performanceApi: typeof performance = performance) {}

  public startSpan(span: TimingSpanWithoutEndTime): void {
    this._createMark(`${span.id}-start`, span.startTime, { devtools: this._commonDevtoolsProperties(span) });
  }

  public endSpan(span: TimingSpanWithEndTime): void {
    const end = this._createMark(`${span.id}-end`, span.endTime, { devtools: this._commonDevtoolsProperties(span) });

    this.performanceApi.measure(span.name, {
      start: span.startTime,
      end: end.startTime,
      detail: this._devtoolsStanzaFromSpan(span),
    });
  }

  public createSpan(span: TimingSpan): void {
    this.performanceApi.measure(span.name, {
      detail: this._devtoolsStanzaFromSpan(span),
      start: span.startTime,
      end: span.endTime,
    });
  }

  // biome-ignore lint/suspicious/noExplicitAny: Need to match ReturnType<F>, which uses any
  public async withSpan<F extends () => Promise<any>>(
    span: TimingSpanWithoutEndTime,
    callback: F,
  ): Promise<AsyncReturnType<F>> {
    const result = await callback();

    this.createSpan({
      ...span,
      endTime: this.performanceApi.now(),
    });

    return result;
  }

  public createEvent(event: TimingEvent): void {
    this._createMark(event.name, event.startTime, this._devtoolsStanzaFromEvent(event));
  }

  public reset(): void {
    this.performanceApi.clearMarks();
    this.performanceApi.clearMeasures();
  }

  private _createMark(
    name: string,
    startTime?: DOMHighResTimeStamp,
    devtoolDetails?: DevtoolsDetails,
  ): PerformanceMark {
    return this.performanceApi.mark(name, {
      startTime,
      detail: devtoolDetails,
    });
  }

  private _devtoolsStanzaFromSpan(span: TimingSpan): DevtoolsDetails {
    return {
      devtools: {
        ...this._commonDevtoolsProperties(span),
        track: span.name,
        trackGroup: span.details?.category,
      },
    };
  }

  private _devtoolsStanzaFromEvent(event: TimingEvent): DevtoolsDetails {
    return {
      devtools: {
        ...this._commonDevtoolsProperties(event),
        dataType: "marker",
      },
    };
  }

  private _commonDevtoolsProperties(spanOrEvent: TimingEvent) {
    return {
      tooltipText: spanOrEvent.details?.message ?? spanOrEvent.name,

      // If a property with an empty RHS is specified, no properties at all are displayed in performance view
      properties: Object.entries(spanOrEvent.details?.properties ?? {}).filter(([k, v]) => k && v),
    };
  }
}
